import { Box } from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"
import { makeStyles } from "tss-react/mui-compat"
import { ConventionCuratorHasNoFallback } from "components/common/ConventionCuratorHasNoFallback"
import { FavoriteVideos } from "components/videos/FavoriteVideos"
import { FeaturedVideos } from "components/videos/FeaturedVideos"
import { RecentlyAddedVideos } from "components/videos/RecentlyAddedVideos"
import { ShuffledVideosByTags } from "components/videos/ShuffledVideosByTags"
import { VideosHeader } from "components/videos/VideosHeader"
import { VideosSearchResults } from "components/videos/VideosSearchResults"
import { useFavoriteVideosPages } from "hooks/useFavoriteVideosPages"
import { useVideosSearch } from "hooks/videos/useVideosSearch"
import ConventionTag from "models/convention-api/v1/ConventionTag"
import ConventionVideo from "models/convention-api/v1/ConventionVideo"
import React, { useMemo, useState } from "react"

interface VideosProps {
  allTags: ConventionTag[]
  shuffledTags: ConventionTag[]
  recentlyAddedVideos: ConventionVideo[]
  featuredVideos: ConventionVideo[]
  showHeader?: boolean
}

const useStyles = makeStyles()(
  (theme) => ({
    container: {
      position: "relative",
      width: "100%",
      height: "100%",
      marginBottom: 40,
    },
    activityIndicatorContainer: {
      zIndex: 1,
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: "flex",
      justifyContent: "center",

      backgroundColor: "#ffffff",
      paddingTop: theme.spacing(5),
    },
  }),
)

export const Videos = (props: VideosProps) => {
  const { classes } = useStyles()
  const { shuffledTags, showHeader = true } = props
  const shuffledTagsLength = useMemo(() => shuffledTags.flat().length, [shuffledTags])

  const { data: video, isValidating: isVideoLoading } = ConventionVideo.useOne({})
  const videosSearch = useVideosSearch()
  const [isShuffleVideosByTagsReady, setIsShuffleVideosByTagsReady] = useState(
    shuffledTagsLength === 0,
  )
  const onShuffledVideosByTagsLoad = () => {
    if (isShuffleVideosByTagsReady) return
    setIsShuffleVideosByTagsReady(true)
  }
  const favoriteVideosPages = useFavoriteVideosPages()

  const isShowingActivityIndicator =
    !favoriteVideosPages.isReady || !isShuffleVideosByTagsReady || isVideoLoading

  if (isVideoLoading) {
    return (
      <Box className={classes.container}>
        <Box className={classes.activityIndicatorContainer}>
          <CircularProgress variant="indeterminate" />
        </Box>
      </Box>
    )
  }

  if (!video) {
    if (!isShuffleVideosByTagsReady) setIsShuffleVideosByTagsReady(true)
    return (
      <Box className={classes.container}>
        <VideosHeader tags={props.allTags} videosSearch={videosSearch} />
        <ConventionCuratorHasNoFallback hasNo="videos uploaded" />
      </Box>
    )
  }

  return (
    <Box
      className={classes.container}
      sx={{ overflow: isShowingActivityIndicator ? "hidden" : "visible" }}>
      {isShowingActivityIndicator && (
        <Box className={classes.activityIndicatorContainer}>
          <CircularProgress variant="indeterminate" />
        </Box>
      )}
      {showHeader && <VideosHeader tags={props.allTags} videosSearch={videosSearch} />}
      {!!videosSearch.filter && <VideosSearchResults filter={videosSearch.filter} />}
      <Box sx={{ display: videosSearch.filter ? "none" : "block" }}>
        {props.featuredVideos.flat().length > 0 && (
          <FeaturedVideos fallbackData={props.featuredVideos} />
        )}
        {props.recentlyAddedVideos.length > 0 && (
          <RecentlyAddedVideos fallbackData={props.recentlyAddedVideos} />
        )}
        {/*check if user has any videos from favorite exhibitors*/}
        {favoriteVideosPages.videos.length > 0 && (
          <FavoriteVideos favoriteVideosPages={favoriteVideosPages} />
        )}
        {shuffledTagsLength > 0 && (
          <ShuffledVideosByTags
            fallbackData={props.shuffledTags}
            onLoad={onShuffledVideosByTagsLoad}
          />
        )}
      </Box>
    </Box>
  )
}
